import { callHostedTool } from './actor-service.js'
import { t } from './portal-utils.js'

const SEEDED_INVITE_WORKSPACE_ID =
  t(import.meta.env.VITE_VALIDATION_INVITE_WORKSPACE_ID) || 'val-workspace-001'
const SEEDED_INVITE_CODE =
  t(import.meta.env.VITE_VALIDATION_INVITE_CODE) || 'val-invite-seeded.val-invite-seed-2026'
const SEEDED_INVITE_WORKSPACE_STORAGE_KEY = 'hd.validation.seededWorkspaceId'
const SEEDED_INVITE_QUERY_STORAGE_PREFIX = 'hd.validation.seededQuery'

function isLocalSeededInviteFixtureEnabled() {
  const hostname = t(window?.location?.hostname).toLowerCase()
  return Boolean(import.meta.env.DEV) && (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1')
}

export function isSeededFixtureWorkspace(workspaceId: string): boolean {
  return isLocalSeededInviteFixtureEnabled() && t(workspaceId) === SEEDED_INVITE_WORKSPACE_ID
}

export function readSeededWorkspaceFromStorage(): string {
  if (!isLocalSeededInviteFixtureEnabled()) return ''
  return t(window?.localStorage?.getItem(SEEDED_INVITE_WORKSPACE_STORAGE_KEY))
}

function writeSeededWorkspaceToStorage(workspaceId: string): void {
  if (!isLocalSeededInviteFixtureEnabled() || !t(workspaceId)) return
  window?.localStorage?.setItem(SEEDED_INVITE_WORKSPACE_STORAGE_KEY, t(workspaceId))
}

function seededQueryStorageKey(workspaceId: string): string {
  return `${SEEDED_INVITE_QUERY_STORAGE_PREFIX}.${t(workspaceId)}`
}

export function readSeededQueryFromStorage(workspaceId: string): Record<string, unknown> | null {
  if (!isSeededFixtureWorkspace(workspaceId)) return null
  const raw = t(window?.localStorage?.getItem(seededQueryStorageKey(workspaceId)))
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

export function writeSeededQueryToStorage(workspaceId: string, intelligence: Record<string, unknown> = {}) {
  if (!isSeededFixtureWorkspace(workspaceId)) return
  const payload = intelligence && typeof intelligence === 'object' ? intelligence : null
  if (!payload) return
  window?.localStorage?.setItem(seededQueryStorageKey(workspaceId), JSON.stringify(payload))
}

export function buildSeededFixtureHostedConfig() {
  return {
    status: 'partial',
    missing: ['hosted_seeded_fixture'],
    guidance: ['Seeded local invite fixture bypassed hosted workspace bootstrap for manual validation.'],
    canRead: true,
    canWrite: false
  }
}

export function buildSeededFixtureIntelligence(question: string, error: string) {
  const now = new Date().toISOString()
  return {
    status: 'grounded',
    state: 'grounded',
    mode: 'single',
    queryId: `seeded-fixture-${Date.now()}`,
    generatedAt: now,
    freshness: 'cached',
    content: `Seeded local fixture response for "${t(question)}". This confirms portal query and reload continuity when hosted invite redemption fixtures are unavailable.`,
    providerStatuses: {
      fixture: {
        status: 'reachable',
        reasonCode: 'seeded_fixture_local_mode'
      }
    },
    warningBundle: {
      entries: error
        ? [{
            provider: 'fixture',
            reasonCode: 'seeded_fixture_local_mode',
            impact: `Hosted query fallback was used due: ${t(error)}`
          }]
        : []
    }
  }
}

/**
 * @param {{ inviteCode?: string, user?: any }} [options]
 */
export async function redeemWorkspaceInviteWithFixture({ inviteCode }: { inviteCode?: string, user?: unknown } = {}) {
  const normalizedInviteCode = t(inviteCode)
  if (!normalizedInviteCode) {
    throw new Error('invite_code_invalid')
  }
  try {
    return await callHostedTool('redeemWorkspaceInvite', { inviteCode: normalizedInviteCode })
  } catch (inviteError) {
    if (
      isLocalSeededInviteFixtureEnabled()
      && normalizedInviteCode === SEEDED_INVITE_CODE
    ) {
      writeSeededWorkspaceToStorage(SEEDED_INVITE_WORKSPACE_ID)
      return {
        workspaceId: SEEDED_INVITE_WORKSPACE_ID,
        role: 'operator',
        status: 'seeded_fixture'
      }
    }
    throw inviteError
  }
}
